import { useEffect, ReactElement, useState } from 'react';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { api } from 'hooks';
import { useRefreshToken } from 'hooks/usePermissions';

interface AxiosInterceptorProps {
  children: ReactElement;
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  hasRetried?: boolean;
}

const AxiosInterceptor = ({ children }: AxiosInterceptorProps) => {
  const [isSet, setIsSet] = useState(false);
  const { attemptTokenRefresh } = useRefreshToken();

  useEffect(() => {
    // Request Interceptor
    const reqInterceptor = api.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token')?.replace(/"/g, '');
        config.headers.authorization = token ? `Bearer ${token}` : '';
        if (!config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json';
        }

        return config;
      },
      (error: AxiosError) => {
        if (error?.response) {
          throw new Error(String(error.response.status));
        }

        return null;
      },
    );

    // Response Interceptor
    const resInterceptor = api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        /**
         * Check if error is 401 and we haven't tried to refresh the token yet.
         *
         * A 401 error means the access token provided is invalid.
         * We can try once to generate an access token from the current refresh
         * token.
         */
        if (error.response?.status === 401 && !originalRequest.hasRetried) {
          originalRequest.hasRetried = true;

          // Attempt to refresh the token
          const refreshed = await attemptTokenRefresh();

          if (refreshed) {
            // Retry the original request with new token
            const token = localStorage.getItem('access_token')?.replace(/"/g, '');
            originalRequest.headers.authorization = `Bearer ${token}`;

            return api(originalRequest);
          }
        } else if (error.name === 'AxiosError') {
          // For other errors, maintain existing logic
          throw error;
        } else if (error.response) {
          throw new Error(String(error.response.status));
        }

        return null;
      },
    );

    setIsSet(true);

    // Cleanup function to remove interceptors
    return () => {
      api.interceptors.request.eject(reqInterceptor);
      api.interceptors.response.eject(resInterceptor);
    };
  }, []);

  if (!isSet) {
    return null;
  }

  return children;
};

export default AxiosInterceptor;
